import type { ReactNode } from "react";
import {
  AttendanceTable,
  type TableColumn,
} from "@/components/tools/printables/attendance-page";
import { fontAttrs } from "@/components/tools/printables/page-kit";
import {
  type HeaderField,
  MUTED,
  SheetHeader,
} from "@/components/tools/printables/sheet-header";
import {
  type DayOptions,
  dayColumns,
  layoutTable,
  monthlyColumns,
  signInColumns,
  type TableLayout,
  type TableOptions,
  weeklyColumns,
} from "@/lib/tool-kit/printables/attendance";
import {
  addDays,
  type CivilDate,
  monthName,
  weekdayNames,
} from "@/lib/tool-kit/printables/calendar";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import type { TextMeasure } from "@/lib/tool-kit/printables/text";

export interface AttendanceStrings {
  numberHead: string;
  nameHead: string;
  absentHead: string;
  tardyHead: string;
  signatureHead: string;
  timeInHead: string;
  timeOutHead: string;
  amHead: string;
  pmHead: string;
  classLabel: string;
  teacherLabel: string;
  dateLabel: string;
  eventLabel: string;
  keyText: string;
}

export interface AttendanceBuild {
  geometry: PageGeometry;
  measure: TextMeasure;
  locale: string;
  strings: AttendanceStrings;
  /** The title as it prints (the month or week is added for those sheets). */
  title: string;
  /** Roster padded with blanks to the minimum rows. */
  names: readonly string[];
  showKey: boolean;
}

const HEADER_HEIGHT = 24;
const TABLE_HEADER = 10;
const FOOTER = 6;

function tableOptions(): TableOptions {
  return {
    headerHeight: HEADER_HEIGHT,
    tableHeader: TABLE_HEADER,
    minRow: 4.8,
    maxRow: 8,
    footer: FOOTER,
  };
}

function Key({ b }: { b: AttendanceBuild }) {
  if (!b.showKey) {
    return null;
  }
  return (
    <text
      fill={MUTED}
      fontSize={3.4}
      x={b.geometry.left}
      y={b.geometry.top + b.geometry.contentHeight - 1.5}
      {...fontAttrs({ kind: "sans" })}
    >
      {b.strings.keyText}
    </text>
  );
}

function sheetNodes(
  b: AttendanceBuild,
  layout: TableLayout,
  fields: HeaderField[],
  title: string,
  columns: readonly TableColumn[],
  right: number
): ReactNode[] {
  return layout.pages.map((page, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional
    <g key={`attendance-${index}`}>
      {page.first ? (
        <SheetHeader
          fields={fields}
          geometry={b.geometry}
          measure={b.measure}
          title={title}
        />
      ) : null}
      <AttendanceTable
        columns={columns}
        measure={b.measure}
        names={b.names}
        page={page}
        right={right}
        rowHeight={layout.rowHeight}
        tableHeader={TABLE_HEADER}
      />
      <Key b={b} />
    </g>
  ));
}

export interface MonthlyBuild extends AttendanceBuild {
  year: number;
  month: number;
  days: DayOptions;
  totals: boolean;
}

/** The monthly register: a column for each school day. */
export function buildMonthly(b: MonthlyBuild): ReactNode[] {
  const days = dayColumns(b.year, b.month, b.days);
  const totalCount = b.totals ? 2 : 0;
  const cols = monthlyColumns(b.geometry, days.length, totalCount);
  const letters = weekdayNames(b.locale, 0, "narrow");
  const columns: TableColumn[] = [
    { col: cols.number, kind: "number", head: b.strings.numberHead },
    { col: cols.name, kind: "name", head: b.strings.nameHead },
    ...days.map((day, index) => ({
      col: cols.days[index] ?? cols.name,
      kind: "cell" as const,
      head: letters[day.weekday] ?? "",
      sub: String(day.day),
      shade: day.off,
    })),
    ...cols.totals.map((col, index) => ({
      col,
      kind: "cell" as const,
      head: index === 0 ? b.strings.absentHead : b.strings.tardyHead,
    })),
  ];
  const layout = layoutTable(b.names.length, b.geometry, tableOptions());
  const fields: HeaderField[] = [
    { label: b.strings.classLabel, share: 5 },
    { label: b.strings.teacherLabel, share: 5 },
  ];
  const title = `${b.title} — ${monthName(b.locale, b.month)} ${b.year}`;
  return sheetNodes(b, layout, fields, title, columns, cols.right);
}

export interface WeeklyBuild extends AttendanceBuild {
  /** The Monday of the week, or null for blank dates. */
  monday: CivilDate | null;
  saturdays: boolean;
  sundays: boolean;
  halves: boolean;
}

/** The weekly register: Monday to Friday (and weekend days if asked), optionally AM and PM. */
export function buildWeekly(b: WeeklyBuild): ReactNode[] {
  const indices = [
    0,
    1,
    2,
    3,
    4,
    ...(b.saturdays ? [5] : []),
    ...(b.sundays ? [6] : []),
  ];
  const names = weekdayNames(b.locale, 1, "short");
  const cols = weeklyColumns(b.geometry, indices.length, b.halves);
  const columns: TableColumn[] = [
    { col: cols.number, kind: "number", head: b.strings.numberHead },
    { col: cols.name, kind: "name", head: b.strings.nameHead },
  ];
  indices.forEach((weekdayIndex, position) => {
    const date = b.monday ? addDays(b.monday, weekdayIndex) : null;
    const dayName = names[weekdayIndex] ?? "";
    const dateText = date
      ? `${date.day} ${monthName(b.locale, date.month, "short")}`
      : "";
    const subs = cols.days[position] ?? [];
    const halfHeads = [b.strings.amHead, b.strings.pmHead];
    subs.forEach((col, half) => {
      columns.push({
        col,
        kind: "cell",
        head: b.halves && date ? `${dayName} ${date.day}` : dayName,
        sub: b.halves ? halfHeads[half] : dateText,
      });
    });
  });
  const layout = layoutTable(b.names.length, b.geometry, tableOptions());
  const fields: HeaderField[] = [
    { label: b.strings.classLabel, share: 5 },
    { label: b.strings.teacherLabel, share: 5 },
  ];
  return sheetNodes(b, layout, fields, b.title, columns, cols.right);
}

/** The sign-in sheet: name, signature, time in and time out. */
export function buildSignIn(b: AttendanceBuild): ReactNode[] {
  const cols = signInColumns(b.geometry);
  const [number, name, signature, timeIn, timeOut] = cols;
  if (!(number && name && signature && timeIn && timeOut)) {
    return [];
  }
  const columns: TableColumn[] = [
    { col: number, kind: "number", head: b.strings.numberHead },
    { col: name, kind: "name", head: b.strings.nameHead },
    { col: signature, kind: "cell", head: b.strings.signatureHead },
    { col: timeIn, kind: "cell", head: b.strings.timeInHead },
    { col: timeOut, kind: "cell", head: b.strings.timeOutHead },
  ];
  const layout = layoutTable(b.names.length, b.geometry, {
    ...tableOptions(),
    minRow: 8,
    maxRow: 11,
  });
  const fields: HeaderField[] = [
    { label: b.strings.dateLabel, share: 3 },
    { label: b.strings.eventLabel, share: 6 },
  ];
  return sheetNodes(
    b,
    layout,
    fields,
    b.title,
    columns,
    b.geometry.left + b.geometry.contentWidth
  );
}
